import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check } from "lucide-react";
import { useLanguage, langPrefix } from "@/i18n/LanguageContext";
import { useUserAuth } from "@/user/UserAuthContext";
import { AUTH_STRINGS } from "@/user/authStrings";
import { AuthCard, Field } from "@/pages/auth/AuthLogin";

/**
 * First-time Google onboarding. Lightest possible: show the prefilled
 * display name + email, let the user confirm/edit the name, and finish.
 */
export default function AuthGoogleComplete() {
  const { lang } = useLanguage();
  const L = AUTH_STRINGS[lang];
  const { getPendingOnboarding, googleComplete } = useUserAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [picture, setPicture] = useState("");
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  // Failure reason from getPendingOnboarding / googleComplete:
  //   null              → no failure
  //   "missing"         → cookie blocked / not received (most actionable)
  //   "expired"         → pending TTL passed
  //   "network"         → fetch threw
  //   "unexpected"      → unrecognised HTTP status
  const [failureReason, setFailureReason] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await getPendingOnboarding();
      if (cancelled) return;
      if (!res.ok) {
        if (res.reason === "missing") setFailureReason("missing");
        else if (res.reason === "network") setFailureReason("network");
        else if (res.reason === "expired") setFailureReason("expired");
        else setFailureReason("unexpected");
        setLoading(false);
        return;
      }
      const pending = res.pending;
      setEmail(pending.email);
      setName(pending.name || "");
      setPicture(pending.picture || "");
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [getPendingOnboarding]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    const trimmed = name.trim();
    if (!trimmed) {
      setError(L.displayName);
      return;
    }
    setSubmitting(true);
    const res = await googleComplete(trimmed, lang);
    setSubmitting(false);
    if (!res.ok) {
      // Promote known reasons to a full-page error so the user sees a
      // concrete next step instead of an inline form error.
      if (res.reason === "missing") {
        setFailureReason("missing");
        return;
      }
      if (res.reason === "expired") {
        setFailureReason("expired");
        return;
      }
      if (res.reason === "network") {
        setFailureReason("network");
        return;
      }
      setError(res.error || L.onboardingUnexpected);
      return;
    }
    navigate(`${langPrefix(lang)}/account` || "/account", { replace: true });
  };

  if (loading) {
    return (
      <AuthCard eyebrow={L.eyebrow} title={L.onboardingTitle} body={L.onboardingBody}>
        <div
          className="mt-6 h-28 flex items-center justify-center text-sm text-[color:var(--sas-ink-2)]"
          data-testid="onboarding-loading"
        >
          {L.callbackWorking}
        </div>
      </AuthCard>
    );
  }

  if (failureReason) {
    const message =
      failureReason === "missing"
        ? L.onboardingCookieBlocked
        : failureReason === "network"
        ? L.onboardingNetwork
        : failureReason === "expired"
        ? L.onboardingExpired
        : L.onboardingUnexpected;
    return (
      <AuthCard eyebrow={L.eyebrow} title={L.onboardingTitle}>
        <div
          className="mt-6 text-[14px] text-[#991b1b] bg-[#fef2f2] border border-[#fecaca] rounded-lg px-3 py-2.5"
          data-testid={`onboarding-${failureReason}`}
          role="alert"
        >
          {message}
        </div>
        <button
          type="button"
          onClick={() => navigate(`${langPrefix(lang)}/auth/login` || "/auth/login", { replace: true })}
          className="sas-btn sas-btn-ghost mt-5"
          data-testid="onboarding-retry"
        >
          {L.retry}
        </button>
      </AuthCard>
    );
  }

  return (
    <AuthCard eyebrow={L.eyebrow} title={L.onboardingTitle} body={L.onboardingBody}>
      <div
        className="mt-6 flex items-center gap-3 p-3 rounded-xl border border-[color:var(--sas-line)] bg-[color:var(--sas-accent-soft)]/30"
        data-testid="onboarding-prefill"
      >
        {picture ? (
          <img
            src={picture}
            alt=""
            aria-hidden
            className="w-10 h-10 rounded-full object-cover"
          />
        ) : (
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center text-[color:var(--sas-accent)]"
            style={{ background: "var(--sas-accent-soft)" }}
            aria-hidden
          >
            {(name || email || "?").slice(0, 1).toUpperCase()}
          </div>
        )}
        <div className="min-w-0">
          <div className="text-[13.5px] font-medium text-[color:var(--sas-ink)] truncate">
            {email}
          </div>
          <div className="text-[11.5px] text-[color:var(--sas-ink-2)]">Google</div>
        </div>
      </div>

      <form onSubmit={onSubmit} className="mt-5 space-y-4" data-testid="onboarding-form" noValidate>
        <Field label={L.displayName}>
          <input
            type="text"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="sas-input"
            data-testid="onboarding-name"
            autoFocus
          />
        </Field>

        {error && (
          <div className="text-[13px] text-red-600" data-testid="onboarding-error">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting || !name.trim()}
          className="sas-btn sas-btn-primary w-full justify-center"
          data-testid="onboarding-submit"
        >
          <Check size={15} aria-hidden />
          {submitting ? L.finishLoading : L.finish}
        </button>
      </form>
    </AuthCard>
  );
}
